import React, { useEffect, useState } from 'react';
import {
    Box, Container, Heading, Text, VStack, HStack, Button, SimpleGrid, Image, Badge, Icon, Flex,
    Accordion, AccordionItem, AccordionButton, AccordionPanel, AccordionIcon, Wrap, WrapItem, Tag,
} from '@chakra-ui/react';
import { Helmet } from 'react-helmet-async';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
    FiCheckCircle, FiClock, FiMapPin, FiPhone, FiTruck, FiFeather, FiDroplet, FiWind, FiPackage, FiCalendar,
    FiGift, FiShield, FiMoon, FiSun, FiAlertCircle, FiMessageSquare,
} from 'react-icons/fi';
import { smsBody, smsHref, textCode } from '../Components/SiteLanding/textOrderLinks';
import GiveawayEntryForm from '../Components/Comforter/GiveawayEntryForm';
import { GIVEAWAY, giveawayState } from './comforterGiveaway';

// Hardcoded for Round Rock (laundry 1). Prices mirror shop.laundry_services;
// the "each additional" rate is the bedding_additional_price the cart already applies.
const LAUNDRY_ID = '1';
const PHONE_DISPLAY = '(512) 782-0428';
const PHONE_TEL = '+15127820428';
const ADDRESS = '900 E Palm Valley Blvd, Suite 1006-1007, Round Rock, TX 78664';
const CANONICAL = 'https://roundrocklaundry.com/comforter-cleaning';
const ADDITIONAL_PRICE = 20;

const IMG = (id, w) => `https://images.unsplash.com/${id}?w=${w}&q=75&auto=format&fit=crop`;
const HERO_IMG = 'photo-1505693416388-ac5ce068fe85';
const LUXE_IMG = 'photo-1616594039964-ae9021a400a0';
const COZY_IMG = 'photo-1522771739844-6a9f6d5f14af';

const PRICES = [
    { name: 'Queen Comforter', price: 25, note: 'Comforters, duvets & quilts up to queen size' },
    { name: 'King Comforter', price: 28, note: 'King & California king, any fill', featured: true },
    { name: 'Down Comforter', price: 28, note: 'Any size — gentle cycle, low-heat drying' },
];

const BUNDLES = [
    { count: 1, total: 25 },
    { count: 2, total: 25 + ADDITIONAL_PRICE },
    { count: 3, total: 25 + ADDITIONAL_PRICE * 2 },
];

const IMPORTANCE = [
    { icon: FiAlertCircle, title: 'Dust mites & allergens', text: 'Dust mites thrive in warm bedding and feed on the skin we shed every night. Their droppings are one of the most common indoor allergy and asthma triggers.' },
    { icon: FiDroplet, title: 'Sweat & body oils', text: 'Every night a little sweat and oil soaks through the cover into the fill. Over months it causes yellowing, odor and a flat, heavy feel.' },
    { icon: FiShield, title: 'Bacteria & mildew', text: 'Moisture trapped in thick fill gives bacteria and mildew a place to grow — especially in humid Central Texas weather.' },
    { icon: FiSun, title: 'Pollen & pet dander', text: 'Pollen and dander ride in on clothes and fur and settle deep in the comforter, where a quick shake can’t reach them.' },
    { icon: FiFeather, title: 'A longer-lasting comforter', text: 'Grime breaks down fibers and flattens fill. Regular cleaning keeps a good comforter lofty and soft for years.' },
    { icon: FiMoon, title: 'Better sleep', text: 'A fresh, clean-smelling bed with fewer nighttime sniffles is simply easier to rest in.' },
];

const HOW_OFTEN = [
    'Every 3–6 months', 'At each change of season', 'When allergies flare up',
    'After an illness at home', 'If pets sleep on the bed', 'Before holiday guests arrive',
];

const CARE = [
    { icon: FiDroplet, title: 'Oversized washers', text: 'Comforters get room to move, so detergent rinses out completely — no stiff, soapy spots.' },
    { icon: FiFeather, title: 'Down & feather safe', text: 'Gentle cycles and patient low-heat drying keep fill lofty instead of clumped.' },
    { icon: FiWind, title: 'Dried all the way through', text: 'We dry until the center is fully dry, so nothing comes home musty.' },
    { icon: FiPackage, title: 'Folded & returned fresh', text: 'Neatly folded and packed, ready for the bed or the linen closet.' },
];

const STEPS = [
    { icon: FiCalendar, title: 'Schedule online', text: 'Pick a pickup time in under a minute, or drop off anytime — we are open 24/7.' },
    { icon: FiTruck, title: 'We pick up', text: 'Leave your bedding at the door. Add notes like "fragrance-free" when you book.' },
    { icon: FiDroplet, title: 'Deep clean', text: 'Washed and dried with fabric-appropriate care by our attendants.' },
    { icon: FiCheckCircle, title: 'Back on your bed', text: 'Delivered fresh and folded. Sleep on clean tonight.' },
];

const AREAS = [
    'Round Rock', 'Brushy Creek', 'Teravista', 'Forest Creek', 'Paloma Lake', 'Avery Ranch',
    'Cedar Park', 'Georgetown', 'Hutto', 'Pflugerville', 'Wells Branch', 'North Austin',
];

const FAQS = [
    { q: 'How often should a comforter be cleaned?', a: 'Most sleep experts suggest every 3–6 months, and always at the change of season. Comforters trap dust, skin cells and allergens that a duvet cover alone does not stop.' },
    { q: 'Can you clean down and feather comforters?', a: 'Yes. Down comforters are washed on a gentle cycle and dried slowly on low heat until the fill is fully dry and lofty again.' },
    { q: 'Why not just wash it at home?', a: 'Most home washers are too small for a queen or king comforter. Cramming it in leaves soap inside and wet spots in the middle that can turn musty. Our large-capacity machines solve that.' },
    { q: 'How does the multi-comforter pricing work?', a: `Your first queen or king comforter is regular price and each additional queen or king comforter in the same order is just $${ADDITIONAL_PRICE}. The discount is applied automatically at checkout.` },
    { q: 'Do you offer pickup and delivery?', a: 'Yes. Schedule a pickup online and we bring everything back clean. Delivery fees depend on distance and are shown before you confirm. You can also drop off at our store 24/7.' },
    { q: 'Can I request fragrance-free or sensitive-skin detergent?', a: 'Absolutely — just add it to your order notes when you book, or let our attendant know at drop-off.' },
    { q: 'How do I enter the comforter giveaway?', a: `Fill out the short form on this page with your name and mobile number. Entry is free — no purchase necessary — and limited to one per person. The winner gets ${GIVEAWAY.prizeDetail.charAt(0).toLowerCase()}${GIVEAWAY.prizeDetail.slice(1)}. See the Official Rules for details.` },
];

const ACCENT = 'teal';
// theme.js repaints solid teal buttons blue, so solid CTAs set their colors explicitly.
const SOLID = { bg: 'teal.600', color: 'white', _hover: { bg: 'teal.700', transform: 'translateY(-1px)', boxShadow: 'lg' } };

const TextToSchedule = ({ ordering, color = 'gray.700', iconColor = 'teal.500', testId }) => {
    if (!ordering?.keyword || !ordering?.smsNumber) return null;
    const code = textCode(ordering);
    return (
        <HStack as="a" href={smsHref(ordering.smsNumber, code)} spacing={1.5} color={color}
            data-testid={testId} _hover={{ textDecoration: 'underline' }}>
            <Icon as={FiMessageSquare} color={iconColor} />
            <Text fontSize="sm">
                Text to schedule — text{' '}
                <Text as="span" fontWeight="800" fontFamily="mono">{smsBody(code).trim()}</Text>
                {' '}to {ordering.smsNumberDisplay}
            </Text>
        </HStack>
    );
};

const SectionTitle = ({ eyebrow, title, sub }) => (
    <VStack spacing={3} textAlign="center" maxW="720px" mx="auto">
        {eyebrow && (
            <Text fontSize="sm" fontWeight="700" letterSpacing="0.12em" textTransform="uppercase" color={`${ACCENT}.600`}>
                {eyebrow}
            </Text>
        )}
        <Heading as="h2" fontSize={{ base: '2xl', md: '4xl' }} color="gray.800" lineHeight="short">{title}</Heading>
        {sub && <Text color="gray.600" fontSize={{ base: 'md', md: 'lg' }}>{sub}</Text>}
    </VStack>
);

const ComforterCleaningPage = () => {
    const params = useParams();
    const laundryId = params.laundryId || LAUNDRY_ID;
    const orderHref = `/${laundryId}`;
    const rulesHref = params.laundryId ? `/${laundryId}${GIVEAWAY.rulesPath}` : GIVEAWAY.rulesPath;
    const giveawayOpen = giveawayState() === 'open';
    const [textOrdering, setTextOrdering] = useState(null);

    useEffect(() => {
        let cancelled = false;
        axios.get(`${process.env.REACT_APP_AWS_API_URL}/api/laundry/get-info`, {
            params: { operation: 'getLaundryInfo', laundryId, isCustomer: true },
        }).then((res) => {
            if (!cancelled && res.data?.textOrdering?.keyword) setTextOrdering(res.data.textOrdering);
        }).catch(() => {});
        return () => { cancelled = true; };
    }, [laundryId]);

    const jsonLd = {
        '@context': 'https://schema.org',
        '@type': 'Service',
        serviceType: 'Comforter cleaning',
        name: 'Comforter Cleaning in Round Rock, TX',
        provider: {
            '@type': 'LocalBusiness',
            name: 'Round Rock Laundry',
            telephone: PHONE_TEL,
            address: {
                '@type': 'PostalAddress',
                streetAddress: '900 E Palm Valley Blvd, Suite 1006-1007',
                addressLocality: 'Round Rock',
                addressRegion: 'TX',
                postalCode: '78664',
                addressCountry: 'US',
            },
        },
        areaServed: AREAS.map((name) => ({ '@type': 'Place', name })),
        offers: PRICES.map((p) => ({ '@type': 'Offer', name: p.name, price: p.price.toFixed(2), priceCurrency: 'USD' })),
    };

    return (
        <Box bg="white" minH="100vh">
            <Helmet>
                <title>Comforter Cleaning in Round Rock, TX | Round Rock Laundry</title>
                <meta
                    name="description"
                    content={`Professional comforter, duvet and down cleaning in Round Rock. Queen $25, King $28, each additional comforter $${ADDITIONAL_PRICE}. Pickup & delivery or 24/7 drop-off.`}
                />
                <link rel="canonical" href={CANONICAL} />
                <meta property="og:title" content="Comforter Cleaning in Round Rock, TX" />
                <meta property="og:description" content="Fresh, fluffy, allergen-free bedding — picked up and delivered." />
                <meta property="og:image" content={IMG(HERO_IMG, 1200)} />
                <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
            </Helmet>

            {/* Season banner */}
            <Box bg="gray.900" color="white" py={2.5} px={4} textAlign="center">
                {giveawayOpen ? (
                    <Text fontSize={{ base: 'sm', md: 'md' }}>
                        <Icon as={FiGift} mr={2} verticalAlign="-2px" color={`${ACCENT}.300`} />
                        <Text as="span" fontWeight="700">Win {GIVEAWAY.prizeShort.toLowerCase()}!</Text>{' '}
                        Free to enter.{' '}
                        <Text as="a" href="#giveaway" textDecoration="underline" fontWeight="600">Enter now</Text>
                    </Text>
                ) : (
                    <Text fontSize={{ base: 'sm', md: 'md' }}>
                        <Text as="span" fontWeight="700">Comforter Season Special:</Text>{' '}
                        every additional queen or king comforter just ${ADDITIONAL_PRICE} — applied automatically.
                    </Text>
                )}
            </Box>

            {/* Hero */}
            <Box position="relative" overflow="hidden" bg="linear-gradient(135deg, #F0FDFA 0%, #E6FFFA 40%, #FDF8F3 100%)">
                <Box position="absolute" top="-120px" right="-120px" w="420px" h="420px" borderRadius="full" bg={`${ACCENT}.100`} opacity={0.6} filter="blur(10px)" />
                <Container maxW="1200px" py={{ base: 12, md: 24 }} position="relative">
                    <Flex direction={{ base: 'column', md: 'row' }} align="center" gap={{ base: 10, md: 14 }}>
                        <VStack align={{ base: 'center', md: 'flex-start' }} spacing={6} flex="1" textAlign={{ base: 'center', md: 'left' }}>
                            <Badge colorScheme={ACCENT} fontSize="sm" px={3} py={1} borderRadius="full" textTransform="none">
                                Round Rock's comforter specialists
                            </Badge>
                            <VStack spacing={1} align={{ base: 'center', md: 'flex-start' }} data-testid="comforter-sale">
                                <Text fontSize={{ base: '2xl', md: '4xl' }} fontWeight="800" letterSpacing="-0.02em" lineHeight="1.05" color="orange.500">
                                    Comforter cleaning sale
                                </Text>
                                <Text fontSize={{ base: '5xl', md: '7xl' }} fontWeight="800" letterSpacing="-0.03em" lineHeight="0.95" color="gray.900">
                                    20% off
                                </Text>
                                <Text fontSize={{ base: 'xl', md: '3xl' }} fontWeight="700" color="gray.800">
                                    Use code{' '}
                                    <Text as="span" fontFamily="mono" bg="orange.100" color="orange.700" px={2} borderRadius="md">COMFY</Text>
                                </Text>
                            </VStack>
                            <Heading as="h1" fontSize={{ base: '4xl', md: '6xl' }} color="gray.900" lineHeight="1.05" letterSpacing="-0.02em">
                                Sleep under something{' '}
                                <Text as="span" color={`${ACCENT}.600`}>truly clean.</Text>
                            </Heading>
                            <Text fontSize={{ base: 'md', md: 'xl' }} color="gray.600" maxW="520px">
                                Professional comforter, duvet and down cleaning for Round Rock and the surrounding
                                neighborhoods. Fresh, fluffy and allergen-free — without hauling it to the laundromat.
                            </Text>
                            <VStack spacing={3} pt={2} align={{ base: 'center', md: 'flex-start' }}>
                                <HStack spacing={4} flexWrap="wrap" justify={{ base: 'center', md: 'flex-start' }}>
                                    <Button as="a" href={orderHref} size="lg" {...SOLID} borderRadius="full" px={9} boxShadow="lg" data-testid="comforter-cta-hero">
                                        Schedule a Pickup
                                    </Button>
                                    <Button as="a" href="#pricing" size="lg" variant="ghost" colorScheme={ACCENT} borderRadius="full" px={6}>
                                        See pricing
                                    </Button>
                                </HStack>
                                <TextToSchedule ordering={textOrdering} testId="comforter-text-hero" />
                            </VStack>
                            <HStack spacing={6} pt={2} color="gray.600" fontSize="sm" flexWrap="wrap" justify={{ base: 'center', md: 'flex-start' }}>
                                <HStack spacing={2}><Icon as={FiClock} color={`${ACCENT}.500`} /><Text>Open 24/7 for drop-off</Text></HStack>
                                <HStack spacing={2}><Icon as={FiTruck} color={`${ACCENT}.500`} /><Text>Pickup & delivery</Text></HStack>
                                {giveawayOpen && (
                                    <HStack as="a" href="#giveaway" spacing={2} color={`${ACCENT}.700`} fontWeight="600" _hover={{ textDecoration: 'underline' }}>
                                        <Icon as={FiGift} /><Text>Win 6 months free</Text>
                                    </HStack>
                                )}
                            </HStack>
                        </VStack>
                        <Box flex="1" maxW="560px" w="100%" position="relative">
                            <Image
                                src={IMG(HERO_IMG, 1000)}
                                alt="Freshly made bed with a clean, fluffy comforter"
                                borderRadius="3xl"
                                boxShadow="2xl"
                                objectFit="cover"
                                w="100%"
                                h={{ base: '280px', md: '440px' }}
                            />
                            <Box
                                position="absolute" bottom={{ base: -6, md: -8 }} left={{ base: 4, md: -8 }}
                                bg="white" borderRadius="2xl" boxShadow="xl" px={5} py={4}
                            >
                                <Text fontSize="xs" color="gray.500" fontWeight="600" textTransform="uppercase" letterSpacing="0.08em">Queen comforter</Text>
                                <HStack align="baseline" spacing={1}>
                                    <Text fontSize="3xl" fontWeight="800" color="gray.900">$25</Text>
                                    <Text fontSize="sm" color="gray.500">· +${ADDITIONAL_PRICE} each extra</Text>
                                </HStack>
                            </Box>
                        </Box>
                    </Flex>
                </Container>
            </Box>

            {/* Why clean */}
            <Container maxW="1200px" py={{ base: 16, md: 24 }} id="why-clean">
                <SectionTitle
                    eyebrow="Why it matters"
                    title="Your comforter is the dirtiest thing on your bed"
                    sub="Duvet covers get washed. The comforter inside quietly collects a season of dust, sweat, skin cells and allergens — every single night. Here's what's building up in there."
                />
                <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} spacing={6} mt={12}>
                    {IMPORTANCE.map((c) => (
                        <HStack key={c.title} align="flex-start" spacing={4} p={6} borderRadius="2xl" bg="white"
                            border="1px solid" borderColor="gray.100" boxShadow="sm">
                            <Flex flexShrink={0} w={12} h={12} align="center" justify="center" borderRadius="full" bg="orange.50" color="orange.500">
                                <Icon as={c.icon} boxSize={6} />
                            </Flex>
                            <Box>
                                <Heading as="h3" size="sm" mb={1.5} color="gray.800">{c.title}</Heading>
                                <Text color="gray.600" fontSize="sm">{c.text}</Text>
                            </Box>
                        </HStack>
                    ))}
                </SimpleGrid>

                <Box mt={12} p={{ base: 6, md: 8 }} borderRadius="2xl" bg={`${ACCENT}.50`} border="1px solid" borderColor={`${ACCENT}.100`}>
                    <Flex direction={{ base: 'column', md: 'row' }} align={{ base: 'flex-start', md: 'center' }} gap={6}>
                        <Box minW={{ md: '220px' }}>
                            <Text fontSize="sm" fontWeight="700" textTransform="uppercase" letterSpacing="0.1em" color={`${ACCENT}.700`}>How often?</Text>
                            <Heading as="h3" size="md" color="gray.800" mt={1}>Clean your comforter:</Heading>
                        </Box>
                        <Wrap spacing={3}>
                            {HOW_OFTEN.map((t) => (
                                <WrapItem key={t}>
                                    <HStack spacing={2} bg="white" px={4} py={2} borderRadius="full" boxShadow="xs">
                                        <Icon as={FiCheckCircle} color={`${ACCENT}.500`} />
                                        <Text fontSize="sm" color="gray.700" fontWeight="500">{t}</Text>
                                    </HStack>
                                </WrapItem>
                            ))}
                        </Wrap>
                    </Flex>
                </Box>
            </Container>

            {/* How we care */}
            <Box bg="gray.50" py={{ base: 16, md: 20 }}>
            <Container maxW="1200px">
                <SectionTitle
                    eyebrow="How we care for it"
                    title="Washing a comforter right takes the right machines"
                    sub="Home washers are too small for most queen and king comforters. Here's what we do differently."
                />
                <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={6} mt={12}>
                    {CARE.map((c) => (
                        <Box key={c.title} p={7} borderRadius="2xl" bg="white" border="1px solid" borderColor="gray.100"
                            _hover={{ boxShadow: 'xl', transform: 'translateY(-4px)' }} transition="all 0.25s">
                            <Flex w={12} h={12} align="center" justify="center" borderRadius="xl" bg={`${ACCENT}.50`} color={`${ACCENT}.600`} mb={4}>
                                <Icon as={c.icon} boxSize={6} />
                            </Flex>
                            <Heading as="h3" size="md" mb={2} color="gray.800">{c.title}</Heading>
                            <Text color="gray.600" fontSize="sm">{c.text}</Text>
                        </Box>
                    ))}
                </SimpleGrid>
            </Container>
            </Box>

            {/* Pricing */}
            <Box id="pricing" bg="gray.900" color="white" py={{ base: 16, md: 24 }}>
                <Container maxW="1200px">
                    <VStack spacing={3} textAlign="center" maxW="720px" mx="auto">
                        <Text fontSize="sm" fontWeight="700" letterSpacing="0.12em" textTransform="uppercase" color={`${ACCENT}.300`}>Pricing</Text>
                        <Heading as="h2" fontSize={{ base: '2xl', md: '4xl' }}>Simple prices. Better in bundles.</Heading>
                        <Text color="gray.300" fontSize={{ base: 'md', md: 'lg' }}>
                            Bring the whole house's bedding — guest room, kids' rooms and the cabin quilt.
                        </Text>
                    </VStack>

                    <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6} mt={12}>
                        {PRICES.map((p) => (
                            <Box key={p.name} p={8} borderRadius="2xl" textAlign="center" position="relative"
                                bg={p.featured ? 'white' : 'whiteAlpha.100'} color={p.featured ? 'gray.900' : 'white'}
                                border="1px solid" borderColor={p.featured ? 'white' : 'whiteAlpha.200'}
                                transform={{ md: p.featured ? 'scale(1.04)' : 'none' }} boxShadow={p.featured ? '2xl' : 'none'}>
                                <Text fontWeight="700" fontSize="lg">{p.name}</Text>
                                <Text fontSize="5xl" fontWeight="800" lineHeight="1.1" my={3}>${p.price}</Text>
                                <Text fontSize="sm" color={p.featured ? 'gray.600' : 'gray.300'} mb={6}>{p.note}</Text>
                                <Button as="a" href={orderHref} w="100%" borderRadius="full"
                                    {...(p.featured
                                        ? SOLID
                                        : { variant: 'outline', colorScheme: ACCENT, color: `${ACCENT}.200`, borderColor: `${ACCENT}.300` })}>
                                    Book now
                                </Button>
                            </Box>
                        ))}
                    </SimpleGrid>

                    <Box mt={14} p={{ base: 6, md: 10 }} borderRadius="3xl" bg={`linear-gradient(120deg, var(--chakra-colors-${ACCENT}-600), var(--chakra-colors-${ACCENT}-400))`}>
                        <Flex direction={{ base: 'column', md: 'row' }} align="center" justify="space-between" gap={8}>
                            <VStack align={{ base: 'center', md: 'flex-start' }} spacing={2} textAlign={{ base: 'center', md: 'left' }}>
                                <Text fontWeight="700" fontSize="sm" textTransform="uppercase" letterSpacing="0.1em" opacity={0.9}>Comforter season special</Text>
                                <Heading as="h3" size="lg">Each additional comforter just ${ADDITIONAL_PRICE}</Heading>
                                <Text opacity={0.9} fontSize="sm">Applies to queen and king comforters in the same order. No code needed.</Text>
                            </VStack>
                            <HStack spacing={{ base: 3, md: 5 }}>
                                {BUNDLES.map((b) => (
                                    <VStack key={b.count} spacing={0} bg="whiteAlpha.300" borderRadius="xl" px={{ base: 4, md: 6 }} py={4} minW="88px">
                                        <Text fontSize="xs" fontWeight="600" opacity={0.9} whiteSpace="nowrap">{b.count} queen{b.count > 1 ? 's' : ''}</Text>
                                        <Text fontSize="2xl" fontWeight="800" data-testid={`bundle-${b.count}`}>${b.total}</Text>
                                    </VStack>
                                ))}
                            </HStack>
                        </Flex>
                    </Box>
                </Container>
            </Box>

            {/* Giveaway */}
            {giveawayState() !== 'upcoming' && (
                <Box id="giveaway" position="relative" overflow="hidden" py={{ base: 16, md: 24 }}
                    bg="linear-gradient(135deg, #FFF7ED 0%, #FEF3C7 45%, #ECFDF5 100%)">
                    <Box position="absolute" bottom="-140px" left="-100px" w="380px" h="380px" borderRadius="full" bg="orange.100" opacity={0.6} />
                    <Container maxW="1200px" position="relative">
                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={{ base: 10, md: 16 }} alignItems="center">
                            <VStack align={{ base: 'center', md: 'flex-start' }} spacing={5} textAlign={{ base: 'center', md: 'left' }}>
                                <Badge colorScheme="orange" fontSize="sm" px={3} py={1} borderRadius="full" textTransform="none">
                                    <Icon as={FiGift} mr={1.5} verticalAlign="-2px" />Comforter Giveaway
                                </Badge>
                                <Heading as="h2" fontSize={{ base: '3xl', md: '5xl' }} color="gray.900" lineHeight="1.1" letterSpacing="-0.01em">
                                    Win 6 months of free comforter cleaning
                                </Heading>
                                <Text fontSize={{ base: 'md', md: 'lg' }} color="gray.700">
                                    One lucky winner gets one comforter cleaned free every month for six months — any size, down included.
                                    Enter with your mobile number; it takes 20 seconds.
                                </Text>
                                <SimpleGrid columns={3} spacing={3} w="100%" maxW="460px">
                                    {[['6', 'free cleanings'], ['1', 'every month'], [GIVEAWAY.prizeValue, 'total value']].map(([big, small]) => (
                                        <VStack key={small} spacing={0} bg="white" borderRadius="xl" py={3} boxShadow="sm">
                                            <Text fontSize="2xl" fontWeight="800" color="orange.500">{big}</Text>
                                            <Text fontSize="xs" color="gray.600" fontWeight="600">{small}</Text>
                                        </VStack>
                                    ))}
                                </SimpleGrid>
                                <Text fontSize="sm" color="gray.600">
                                    No purchase necessary. Ends {GIVEAWAY.endLabel}. Winner drawn {GIVEAWAY.drawLabel}.{' '}
                                    <Text as="a" href={rulesHref} color={`${ACCENT}.700`} textDecoration="underline" data-testid="giveaway-rules-link">
                                        Official Rules
                                    </Text>
                                </Text>
                            </VStack>
                            <GiveawayEntryForm rulesHref={rulesHref} />
                        </SimpleGrid>
                    </Container>
                </Box>
            )}

            {/* How it works */}
            <Container maxW="1200px" py={{ base: 16, md: 24 }}>
                <SectionTitle eyebrow="How it works" title="Clean bedding without lifting a finger" />
                <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={8} mt={12}>
                    {STEPS.map((s, i) => (
                        <VStack key={s.title} spacing={3} textAlign="center" px={2}>
                            <Box position="relative">
                                <Flex w={16} h={16} align="center" justify="center" borderRadius="full" bg={`${ACCENT}.600`} color="white" boxShadow="lg">
                                    <Icon as={s.icon} boxSize={7} />
                                </Flex>
                                <Flex position="absolute" top={-1} right={-1} w={6} h={6} borderRadius="full" bg="white" border="2px solid"
                                    borderColor={`${ACCENT}.600`} align="center" justify="center" fontSize="xs" fontWeight="800" color={`${ACCENT}.700`}>
                                    {i + 1}
                                </Flex>
                            </Box>
                            <Heading as="h3" size="md" color="gray.800">{s.title}</Heading>
                            <Text color="gray.600" fontSize="sm">{s.text}</Text>
                        </VStack>
                    ))}
                </SimpleGrid>
            </Container>

            {/* Lifestyle split */}
            <Box bg="#FDF8F3" py={{ base: 16, md: 24 }}>
                <Container maxW="1200px">
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={{ base: 10, md: 16 }} alignItems="center">
                        <SimpleGrid columns={2} spacing={4}>
                            <Image src={IMG(LUXE_IMG, 600)} alt="Elegant bedroom with crisp white bedding" borderRadius="2xl" objectFit="cover" h={{ base: '220px', md: '340px' }} w="100%" boxShadow="lg" />
                            <Image src={IMG(COZY_IMG, 600)} alt="Cozy bed with soft pillows and a clean comforter" borderRadius="2xl" objectFit="cover" h={{ base: '220px', md: '340px' }} w="100%" boxShadow="lg" mt={{ base: 8, md: 14 }} />
                        </SimpleGrid>
                        <VStack align="flex-start" spacing={5}>
                            <Text fontSize="sm" fontWeight="700" letterSpacing="0.12em" textTransform="uppercase" color={`${ACCENT}.600`}>The fresh-bed feeling</Text>
                            <Heading as="h2" fontSize={{ base: '2xl', md: '4xl' }} color="gray.800" lineHeight="short">
                                Hotel-fresh bedding, right at home
                            </Heading>
                            <Text color="gray.600" fontSize="lg">
                                Perfect before holiday guests arrive, when swapping to winter bedding, or after allergy season.
                                We treat every comforter like it is going on our own bed.
                            </Text>
                            <VStack align="flex-start" spacing={3} pt={1}>
                                {['Fragrance-free and sensitive-skin options on request', 'Duvets, quilts, weighted blankets and pillows too', 'Order updates by text or email — your choice'].map((t) => (
                                    <HStack key={t} spacing={3} align="flex-start">
                                        <Icon as={FiCheckCircle} color={`${ACCENT}.500`} mt={1} />
                                        <Text color="gray.700">{t}</Text>
                                    </HStack>
                                ))}
                            </VStack>
                            <Button as="a" href={orderHref} size="lg" {...SOLID} borderRadius="full" px={8} mt={2}>
                                Book your comforter clean
                            </Button>
                        </VStack>
                    </SimpleGrid>
                </Container>
            </Box>

            {/* Service areas */}
            <Container maxW="1000px" py={{ base: 16, md: 20 }} textAlign="center">
                <SectionTitle
                    eyebrow="Service area"
                    title="Serving Round Rock and nearby neighborhoods"
                    sub="Pickup and delivery across Williamson County and North Austin. Delivery fees depend on distance and are shown before you confirm."
                />
                <Wrap justify="center" spacing={3} mt={8}>
                    {AREAS.map((a) => (
                        <WrapItem key={a}>
                            <Tag size="lg" borderRadius="full" variant="subtle" colorScheme={ACCENT} px={4} py={2}>
                                <Icon as={FiMapPin} mr={2} />{a}
                            </Tag>
                        </WrapItem>
                    ))}
                </Wrap>
            </Container>

            {/* FAQ */}
            <Box bg="gray.50" py={{ base: 16, md: 20 }}>
                <Container maxW="820px">
                    <SectionTitle eyebrow="FAQ" title="Comforter cleaning questions" />
                    <Accordion allowToggle mt={10} bg="white" borderRadius="2xl" boxShadow="sm" overflow="hidden">
                        {FAQS.map((f) => (
                            <AccordionItem key={f.q} borderColor="gray.100">
                                <h3>
                                    <AccordionButton py={5} px={6} _hover={{ bg: 'gray.50' }}>
                                        <Box flex="1" textAlign="left" fontWeight="600" color="gray.800">{f.q}</Box>
                                        <AccordionIcon />
                                    </AccordionButton>
                                </h3>
                                <AccordionPanel px={6} pb={5} color="gray.600">{f.a}</AccordionPanel>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </Container>
            </Box>

            {/* Final CTA */}
            <Box position="relative" overflow="hidden" bg="gray.900" color="white" py={{ base: 16, md: 24 }}>
                <Image src={IMG(HERO_IMG, 1600)} alt="" position="absolute" inset={0} w="100%" h="100%" objectFit="cover" opacity={0.18} />
                <Container maxW="800px" position="relative" textAlign="center">
                    <VStack spacing={5}>
                        <VStack spacing={1} data-testid="comforter-sale-footer">
                            <Heading as="h2" fontSize={{ base: '3xl', md: '5xl' }} lineHeight="short">Comforter cleaning sale</Heading>
                            <Text fontSize={{ base: '4xl', md: '6xl' }} fontWeight="800" lineHeight="1" color="orange.300">20% off</Text>
                            <Text fontSize={{ base: 'xl', md: '2xl' }} fontWeight="700">
                                Use code <Text as="span" fontFamily="mono">COMFY</Text>
                            </Text>
                        </VStack>
                        <Text color="gray.300" fontSize="lg">Schedule a pickup in under a minute or drop off anytime, day or night.</Text>
                        <VStack spacing={3}>
                            <HStack spacing={4} flexWrap="wrap" justify="center">
                                <Button as="a" href={orderHref} size="lg" {...SOLID} borderRadius="full" px={10} data-testid="comforter-cta-footer">
                                    Schedule a Pickup
                                </Button>
                                <Button as="a" href={`tel:${PHONE_TEL}`} size="lg" variant="outline" colorScheme="whiteAlpha" color="white" borderRadius="full" px={8} leftIcon={<FiPhone />}>
                                    {PHONE_DISPLAY}
                                </Button>
                            </HStack>
                            <TextToSchedule ordering={textOrdering} color="gray.200" iconColor="teal.300" testId="comforter-text-footer" />
                        </VStack>
                        <VStack spacing={1} pt={4} color="gray.400" fontSize="sm">
                            <HStack spacing={2}><Icon as={FiMapPin} /><Text>{ADDRESS}</Text></HStack>
                            <HStack spacing={2}><Icon as={FiClock} /><Text>Open 24/7</Text></HStack>
                        </VStack>
                    </VStack>
                </Container>
            </Box>
        </Box>
    );
};

export default ComforterCleaningPage;
